/** Buttons built on the global `.btn` classes (styles/global.css). */
import clsx from 'clsx';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { Spinner } from './Spinner.js';

export type ButtonVariant = 'default' | 'primary' | 'accent' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

/** Class list for elements that look like a button (e.g. <a> or <Link>). */
export function buttonClass(variant: ButtonVariant = 'default', size: ButtonSize = 'md', extra?: string): string {
  return clsx('btn', variant !== 'default' && `btn-${variant}`, size !== 'md' && `btn-${size}`, extra);
}

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  /** Shows a spinner and disables the button. */
  busy?: boolean;
}

export function Button({ variant = 'default', size = 'md', icon, busy = false, className, children, disabled, type = 'button', ...rest }: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      className={buttonClass(variant, size, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      {busy ? <Spinner size={16} label={null} /> : icon}
      {children}
    </button>
  );
}

export interface IconButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  /** Accessible name (also shown as tooltip). */
  label: string;
  icon: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function IconButton({ label, icon, variant = 'ghost', size = 'md', className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button {...rest} type={type} className={buttonClass(variant, size, clsx('btn-icon', className))} aria-label={label} title={label}>
      {icon}
    </button>
  );
}
